import {
  getPortalPocketBaseUrl,
  getPortalPbCustomerJson,
  getPortalPbToken,
  isFirmaDemoPortalTenant,
  isLaborPortalHostname,
} from '../../constants';
import { parseAccountKind } from './portalCustomerAccess';
import type { ReasoningTrace } from './reasoningTrace';

export const FEEDBACK_KATEGORIEN = ['Anregung', 'Problem', 'Verbesserung'] as const;
export type FeedbackKategorie = (typeof FEEDBACK_KATEGORIEN)[number];

export const FEEDBACK_RATINGS = ['gut', 'schlecht'] as const;
export type FeedbackRating = (typeof FEEDBACK_RATINGS)[number];

/** Speakable Lookup-Code: FB-YYYYMMDD-XXXX (Crockford-ähnlich, ohne 0/O/1/I/L). */
export const FEEDBACK_DIAG_CODE_RE = /^FB-\d{8}-[2-9A-HJ-NP-Z]{4}$/;

const DIAG_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';

/** Nur Code-artige Tokens (Routing-/Warn-/Error-Codes), kein Freitext. */
const CODE_TOKEN_RE = /^[A-Za-z][A-Za-z0-9_]{1,79}$/;

export interface FeedbackDiagPipelineStep {
  step: string;
  ok: boolean;
  ms?: number;
}

/** Internes Diagnose-JSON (Schema v1) — PII-arm, fest keyed. */
export interface FeedbackDiagJson {
  v: 1;
  mode?: string;
  workflow_step?: string;
  heuristic_reason?: string;
  confidence?: number;
  pipeline?: FeedbackDiagPipelineStep[];
  warning_codes?: string[];
  error_code?: string;
  viewport?: { w: number; h: number };
  ua_family?: string;
  portal_build?: string;
  kanzlei_id?: string;
}

export interface FeedbackDiagMeta {
  mode?: string;
  workflow_step?: string;
  heuristic_reason?: string;
  confidence?: number;
  pipeline?: FeedbackDiagPipelineStep[];
  warning_codes?: string[];
  error_code?: string;
  viewport?: { w: number; h: number };
  ua_family?: string;
  portal_build?: string;
  kanzlei_id?: string;
}

export interface CustomerFeedbackInput {
  customerId: string;
  kategorie: FeedbackKategorie;
  nachricht?: string;
  rating?: FeedbackRating;
  /** Client-Klickzeit ISO-8601 UTC — parallel zu PB `created`. */
  client_submitted_at?: string;
  execution_id?: string;
  request_id?: string;
  intent?: string;
  path?: string;
  /** Optional vorberechneter Code (Tests); sonst immer neu erzeugt. */
  diag_code?: string;
  /** Trace-/Client-Hinweise — werden PII-arm in `diag_json` verdichtet. */
  diag?: FeedbackDiagMeta;
}

export interface CustomerFeedbackRecord {
  customer_id: string;
  kategorie: FeedbackKategorie;
  nachricht?: string;
  rating?: FeedbackRating;
  client_submitted_at?: string;
  execution_id?: string;
  request_id?: string;
  intent?: string;
  path?: string;
  /** Interner Lookup-Code — nicht für Kunden-UI. */
  diag_code: string;
  /** Strukturiertes Diagnose-JSON (Schema v1), String für PB text-Feld. */
  diag_json: string;
}

/** Exakter Client-Zeitpunkt als ISO-8601 UTC. */
export function nowClientSubmittedAtIso(date: Date = new Date()): string {
  return date.toISOString();
}

/**
 * Anzeige Europe/Vienna, Sekunden genau — z. B. `15.08.2026, 19:08:32`.
 * Ungültige Inputs → leerer String.
 */
export function formatFeedbackTimestampVienna(isoOrDate: string | Date): string {
  const date = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('de-AT', {
    timeZone: 'Europe/Vienna',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('day')}.${get('month')}.${get('year')}, ${get('hour')}:${get('minute')}:${get('second')}`;
}

const LEGACY_KATEGORIE: Record<string, FeedbackKategorie> = {
  Wunsch: 'Anregung',
  Kommentar: 'Verbesserung',
  Problem: 'Problem',
  Anregung: 'Anregung',
  Verbesserung: 'Verbesserung',
};

export function mapFeedbackKategorie(raw: string): FeedbackKategorie {
  return LEGACY_KATEGORIE[raw.trim()] ?? 'Problem';
}

export function defaultKategorieForRating(rating: FeedbackRating): FeedbackKategorie {
  return rating === 'gut' ? 'Anregung' : 'Problem';
}

/** Labor-Demo immer; Firma nur FIRMA_DEMO / demo_prospect. Keine normalen Kunden. */
export function isPortalDemoSupportEnabled(opts?: {
  hostname?: string;
  customerStatus?: string;
  customerEmail?: string;
  accountKind?: string;
}): boolean {
  const host =
    opts?.hostname?.trim() ||
    (typeof window !== 'undefined' ? window.location.hostname : '');
  if (host && isLaborPortalHostname(host)) return true;
  if (isFirmaDemoPortalTenant(host, opts?.customerStatus, opts?.customerEmail)) return true;
  return parseAccountKind(opts?.accountKind) === 'demo_prospect';
}

export function readPortalDemoSupportContext(): {
  customerId: string | null;
  customerStatus?: string;
  customerEmail?: string;
  accountKind?: string;
  kanzleiId?: string;
} {
  try {
    const raw = getPortalPbCustomerJson();
    if (!raw) return { customerId: null };
    const parsed = JSON.parse(raw) as {
      id?: unknown;
      status?: unknown;
      email?: unknown;
      account_kind?: unknown;
      kanzlei_id?: unknown;
    };
    const customerId = typeof parsed.id === 'string' && parsed.id.trim() ? parsed.id.trim() : null;
    const kanzleiRaw = typeof parsed.kanzlei_id === 'string' ? parsed.kanzlei_id.trim() : '';
    const kanzleiId = sanitizeCodeToken(kanzleiRaw, 80) || undefined;
    return {
      customerId,
      customerStatus: typeof parsed.status === 'string' ? parsed.status : undefined,
      customerEmail: typeof parsed.email === 'string' ? parsed.email : undefined,
      accountKind: typeof parsed.account_kind === 'string' ? parsed.account_kind : undefined,
      kanzleiId,
    };
  } catch {
    return { customerId: null };
  }
}

function clip(value: string | undefined, max: number): string {
  return String(value ?? '').trim().slice(0, max);
}

function sanitizeCodeToken(raw: string | undefined, max = 80): string {
  const value = clip(raw, max);
  if (!value || !CODE_TOKEN_RE.test(value)) return '';
  return value;
}

function randomDiagSuffix(length = 4): string {
  const out: string[] = [];
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    for (let i = 0; i < length; i += 1) {
      out.push(DIAG_CODE_ALPHABET[bytes[i]! % DIAG_CODE_ALPHABET.length]!);
    }
    return out.join('');
  }
  for (let i = 0; i < length; i += 1) {
    out.push(DIAG_CODE_ALPHABET[Math.floor(Math.random() * DIAG_CODE_ALPHABET.length)]!);
  }
  return out.join('');
}

/** `FB-20260815-H7K3` — Datum UTC + 4 speakable Zeichen. */
export function generateFeedbackDiagCode(date: Date = new Date()): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `FB-${y}${m}${d}-${randomDiagSuffix(4)}`;
}

export function isValidFeedbackDiagCode(raw: string | undefined): boolean {
  return FEEDBACK_DIAG_CODE_RE.test(String(raw ?? '').trim());
}

/** Grob UA-Familie — kein voller User-Agent-String. */
export function detectUaFamily(userAgent?: string): string {
  const ua = String(userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '')).trim();
  if (!ua) return '';
  if (/Edg\//i.test(ua)) return 'Edge';
  if (/OPR\/|Opera/i.test(ua)) return 'Opera';
  if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) return 'Chrome';
  if (/Chromium/i.test(ua)) return 'Chromium';
  if (/Firefox\//i.test(ua)) return 'Firefox';
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return 'Safari';
  return 'Other';
}

export function collectClientDiagHints(): Pick<
  FeedbackDiagMeta,
  'viewport' | 'ua_family' | 'portal_build' | 'kanzlei_id'
> {
  const hints: FeedbackDiagMeta = {};
  if (typeof window !== 'undefined') {
    const w = Math.round(Number(window.innerWidth));
    const h = Math.round(Number(window.innerHeight));
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      hints.viewport = { w: Math.min(w, 10000), h: Math.min(h, 10000) };
    }
  }
  const uaFamily = detectUaFamily();
  if (uaFamily) hints.ua_family = uaFamily;
  // portal_build/SHA: nur wenn der Client später ein echtes Build-Label hat — kein Fake aus anderen VITE-*.
  const session = readPortalDemoSupportContext();
  if (session.kanzleiId) hints.kanzlei_id = session.kanzleiId;
  return hints;
}

export function diagMetaFromTrace(trace: ReasoningTrace | null | undefined): FeedbackDiagMeta {
  if (!trace) return {};
  const meta: FeedbackDiagMeta = {};
  const mode = sanitizeCodeToken(trace.mode, 80);
  if (mode) meta.mode = mode;
  const workflow = sanitizeCodeToken(trace.workflow_step, 80);
  if (workflow) meta.workflow_step = workflow;
  const heuristic = sanitizeCodeToken(trace.heuristic_reason, 80);
  if (heuristic) meta.heuristic_reason = heuristic;
  if (typeof trace.confidence === 'number' && Number.isFinite(trace.confidence)) {
    meta.confidence = Math.max(0, Math.min(1, trace.confidence));
  }
  const pipeline = (trace.pipeline ?? [])
    .map((step): FeedbackDiagPipelineStep | null => {
      const name = sanitizeCodeToken(step.step, 80);
      if (!name) return null;
      const entry: FeedbackDiagPipelineStep = { step: name, ok: step.ok === true };
      if (typeof step.ms === 'number' && Number.isFinite(step.ms) && step.ms >= 0) {
        entry.ms = Math.min(Math.round(step.ms), 3_600_000);
      }
      return entry;
    })
    .filter((entry): entry is FeedbackDiagPipelineStep => entry != null)
    .slice(0, 40);
  if (pipeline.length > 0) meta.pipeline = pipeline;
  const warning_codes = (trace.warnings ?? [])
    .map((w) => sanitizeCodeToken(w, 80))
    .filter(Boolean)
    .slice(0, 20);
  if (warning_codes.length > 0) meta.warning_codes = warning_codes;
  const errorCode = sanitizeCodeToken(trace.error ?? undefined, 80);
  if (errorCode) meta.error_code = errorCode;
  return meta;
}

export function buildFeedbackDiagJson(
  meta?: FeedbackDiagMeta | null,
  clientHints?: FeedbackDiagMeta | null,
): FeedbackDiagJson {
  const merged: FeedbackDiagMeta = { ...(clientHints ?? {}), ...(meta ?? {}) };
  const out: FeedbackDiagJson = { v: 1 };
  const mode = sanitizeCodeToken(merged.mode, 80);
  if (mode) out.mode = mode;
  const workflow = sanitizeCodeToken(merged.workflow_step, 80);
  if (workflow) out.workflow_step = workflow;
  const heuristic = sanitizeCodeToken(merged.heuristic_reason, 80);
  if (heuristic) out.heuristic_reason = heuristic;
  if (typeof merged.confidence === 'number' && Number.isFinite(merged.confidence)) {
    out.confidence = Math.max(0, Math.min(1, merged.confidence));
  }
  if (Array.isArray(merged.pipeline) && merged.pipeline.length > 0) {
    out.pipeline = merged.pipeline
      .map((step): FeedbackDiagPipelineStep | null => {
        const name = sanitizeCodeToken(step.step, 80);
        if (!name) return null;
        const entry: FeedbackDiagPipelineStep = { step: name, ok: step.ok === true };
        if (typeof step.ms === 'number' && Number.isFinite(step.ms) && step.ms >= 0) {
          entry.ms = Math.min(Math.round(step.ms), 3_600_000);
        }
        return entry;
      })
      .filter((entry): entry is FeedbackDiagPipelineStep => entry != null)
      .slice(0, 40);
    if (!out.pipeline.length) delete out.pipeline;
  }
  if (Array.isArray(merged.warning_codes) && merged.warning_codes.length > 0) {
    const codes = merged.warning_codes.map((c) => sanitizeCodeToken(c, 80)).filter(Boolean).slice(0, 20);
    if (codes.length > 0) out.warning_codes = codes;
  }
  const errorCode = sanitizeCodeToken(merged.error_code, 80);
  if (errorCode) out.error_code = errorCode;
  if (
    merged.viewport &&
    Number.isFinite(merged.viewport.w) &&
    Number.isFinite(merged.viewport.h) &&
    merged.viewport.w > 0 &&
    merged.viewport.h > 0
  ) {
    out.viewport = {
      w: Math.min(Math.round(merged.viewport.w), 10000),
      h: Math.min(Math.round(merged.viewport.h), 10000),
    };
  }
  const ua = clip(merged.ua_family, 40);
  if (ua && CODE_TOKEN_RE.test(ua)) out.ua_family = ua;
  const build = clip(merged.portal_build, 80);
  if (build && !/[&=?]/.test(build)) out.portal_build = build;
  const kanzleiId = sanitizeCodeToken(merged.kanzlei_id, 80);
  if (kanzleiId) out.kanzlei_id = kanzleiId;
  return out;
}

export function buildCustomerFeedbackRecord(input: CustomerFeedbackInput): CustomerFeedbackRecord {
  const customer_id = clip(input.customerId, 80);
  if (!customer_id) {
    throw new Error('Feedback braucht eine angemeldete Session.');
  }
  const kategorie = mapFeedbackKategorie(input.kategorie);
  const nachricht = clip(input.nachricht, 1000);
  const providedCode = clip(input.diag_code, 24);
  const diag_code = isValidFeedbackDiagCode(providedCode)
    ? providedCode
    : generateFeedbackDiagCode();
  const diagJson = buildFeedbackDiagJson(input.diag, collectClientDiagHints());
  const record: CustomerFeedbackRecord = {
    customer_id,
    kategorie,
    diag_code,
    diag_json: JSON.stringify(diagJson),
  };
  if (nachricht) record.nachricht = nachricht;
  if (input.rating && (FEEDBACK_RATINGS as readonly string[]).includes(input.rating)) {
    record.rating = input.rating;
  }
  const clientSubmittedAt = clip(input.client_submitted_at, 40);
  if (clientSubmittedAt && !Number.isNaN(Date.parse(clientSubmittedAt))) {
    record.client_submitted_at = new Date(clientSubmittedAt).toISOString();
  }
  const executionId = clip(input.execution_id, 200);
  if (executionId) record.execution_id = executionId;
  const requestId = clip(input.request_id, 200);
  if (requestId) record.request_id = requestId;
  const intent = clip(input.intent, 200);
  if (intent) record.intent = intent;
  const path = clip(input.path, 400);
  if (path) record.path = path;
  return record;
}

export function feedbackFromTrace(
  trace: ReasoningTrace | null | undefined,
): Pick<CustomerFeedbackInput, 'execution_id' | 'request_id' | 'intent' | 'path' | 'diag'> {
  if (!trace) return {};
  return {
    ...(trace.execution_id ? { execution_id: trace.execution_id } : {}),
    ...(trace.request_id ? { request_id: trace.request_id } : {}),
    ...(trace.intent ? { intent: trace.intent } : {}),
    ...(trace.path ? { path: trace.path } : {}),
    diag: diagMetaFromTrace(trace),
  };
}

export async function submitCustomerFeedback(input: CustomerFeedbackInput): Promise<void> {
  const record = buildCustomerFeedbackRecord(input);
  const token = getPortalPbToken();
  if (!token) {
    throw new Error('Keine Session — bitte neu anmelden.');
  }
  const res = await fetch(`${getPortalPocketBaseUrl().replace(/\/$/, '')}/api/collections/customer_feedback/records`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(record),
  });
  if (res.ok) return;
  if (res.status === 404) {
    throw new Error('Feedback-Sammlung ist auf diesem Stack noch nicht eingerichtet.');
  }
  let detail = `HTTP ${res.status}`;
  try {
    const data = (await res.json()) as { message?: unknown };
    if (typeof data.message === 'string' && data.message.trim()) {
      detail = data.message.trim().slice(0, 180);
    }
  } catch {
    /* ignore */
  }
  throw new Error(`Feedback konnte nicht gespeichert werden (${detail}).`);
}
